"use client";
import { FC } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import DisabilitySupportCard from "@/app/(landing)/mental-health/components/disability-support-card";
import { motion } from "framer-motion";

/**
 * Props for `DisabilitySupportSection`.
 */
export type DisabilitySupportSectionProps =
  SliceComponentProps<Content.DisabilitySupportSectionSlice>;

/**
 * Component for "DisabilitySupportSection" Slices.
 */
const DisabilitySupportSection: FC<DisabilitySupportSectionProps> = ({
  slice,
}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === "default" ? (
        <Bounded className="py-20">
          {/* heading */}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            <PrismicRichText
              field={slice.primary.heading}
              components={{
                heading1: ({ children }) => (
                  <h1 className="text-primary text-4xl lg:text-5xl font-bold md:font-semibold text-center mb-8">
                    {children}
                  </h1>
                ),
                heading2: ({ children }) => (
                  <h2 className="text-primary text-4xl lg:text-5xl font-bold md:font-semibold text-center mb-8">
                    {children}
                  </h2>
                ),
                heading3: ({ children }) => (
                  <h3 className="text-primary text-4xl lg:text-5xl font-bold md:font-semibold text-center mb-8">
                    {children}
                  </h3>
                ),
                heading4: ({ children }) => (
                  <h4 className="text-primary text-4xl lg:text-5xl font-bold md:font-semibold text-center mb-8">
                    {children}
                  </h4>
                ),
                heading5: ({ children }) => (
                  <h5 className="text-primary text-4xl lg:text-5xl font-bold md:font-semibold text-center mb-8">
                    {children}
                  </h5>
                ),
                heading6: ({ children }) => (
                  <h6 className="text-primary text-4xl lg:text-5xl font-bold md:font-semibold text-center mb-8">
                    {children}
                  </h6>
                ),
                label: ({ node, children }) => {
                  if (node.data.label === "Highlight") {
                    return <span className="text-secondary">{children}</span>;
                  }
                },
              }}
            />
          </motion.div>

          {/* cards */}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1 }}
          >
            {slice.primary.disability_support_cards.map((item, index) => (
              <DisabilitySupportCard key={index} data={item} />
            ))}
          </motion.div>
        </Bounded>
      ) : null}
    </section>
  );
};

export default DisabilitySupportSection;
